<template>
  <v-row>
    <v-dialog v-model="showDialog" scrollable max-width="950px">
      <v-skeleton-loader
        v-if="loading"
        type="article, actions"
      ></v-skeleton-loader>
      <v-card v-if="!loading">
        <v-card-title>
          <v-panel>
            <span style="font-size: 20px;">
              {{
                !program.id
                  ? $t("wkt.admin.training-program.new-window.title")
                  : $t("wkt.admin.training-program.edit-window.title")
              }}
            </span>
          </v-panel>
        </v-card-title>
        <v-card-text @wheel="onWheel">
          <v-container>
            <v-form ref="form">
              <v-row>
                <v-col cols="12" md="4">
                  <v-text-field
                    v-model="program.reference"
                    :label="$t('wkt.admin.training-program.edit-window.reference')"
                    autocomplete="off"
                    maxlength="100"
                    counter="100"
                    :disabled="isReadOnly"
                  />
                </v-col>
                <v-col cols="12" md="8">
                  <v-text-field
                    v-model="program.name"
                    :label="$t('wkt.admin.training-program.edit-window.name')"
                    autocomplete="off"
                    :rules="[validationRules.required]"
                    maxlength="150"
                    counter="150"
                    :disabled="isReadOnly"
                  />
                </v-col>
              </v-row>
              <v-row>
                <v-col cols="12" md="3" class="d-flex align-center">
                  <v-checkbox
                    v-model="program.isSequential"
                    :label="$t('wkt.admin.training-program.edit-window.is-sequential')"
                    :disabled="isReadOnly"
                  />
                </v-col>
                <v-col cols="12" md="3" class="d-flex align-center">
                  <v-checkbox
                    v-model="program.isActive"
                    :label="$t('wkt.admin.training-program.edit-window.is-active')"
                    :disabled="isReadOnly"
                  />
                </v-col>
              </v-row>
              <v-row>
                <v-col cols="12" md="6">
                  <v-textarea
                    v-model="program.description"
                    :label="$t('wkt.admin.training-program.edit-window.description')"
                    auto-grow
                    rows="3"
                    maxlength="1000"
                    counter="1000"
                    :disabled="isReadOnly"
                  />
                </v-col>
                <v-col cols="12" md="6">
                  <v-textarea
                    v-model="program.objective"
                    :label="$t('wkt.admin.training-program.edit-window.objective')"
                    auto-grow
                    rows="3"
                    maxlength="1000"
                    counter="1000"
                    :disabled="isReadOnly"
                  />
                </v-col>
              </v-row>
            </v-form>

            <v-divider class="my-4"></v-divider>

            <v-row>
              <v-col cols="12">
                <span style="font-size: 16px; font-weight: 500;">
                  {{ $t("wkt.admin.training-program.edit-window.courses-section") }}
                </span>
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="12" md="9">
                <v-autocomplete
                  v-model="selectedCourseId"
                  :items="availableCourses"
                  item-text="courseLabel"
                  item-value="id"
                  :label="$t('wkt.admin.training-program.edit-window.add-course')"
                  :disabled="isReadOnly"
                  clearable
                />
              </v-col>
              <v-col cols="12" md="3" class="d-flex align-center">
                <v-btn
                  color="#185EA9"
                  text
                  :disabled="isReadOnly || !selectedCourseId"
                  @click="addCourse()"
                >
                  {{ $t("wkt.admin.training-program.edit-window.add-course-button").toUpperCase() }}
                </v-btn>
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="12">
                <v-data-table
                  :headers="courseHeaders"
                  :items="selectedCourseRows"
                  :loading="loading"
                  :items-per-page="-1"
                  hide-default-footer
                  disable-pagination
                >
                  <template v-slot:[`item.order`]="{ item }">
                    {{ item.order + 1 }}
                  </template>
                  <template v-slot:[`item.actions`]="{ item }">
                    <v-btn
                      text
                      color="#185EA9"
                      style="min-width: 36px; padding: 0;"
                      :disabled="isReadOnly || item.order === 0"
                      @click="moveCourse(item.order, -1)"
                    >
                      <v-icon color="#185EA9">arrow_upward</v-icon>
                    </v-btn>
                    <v-btn
                      text
                      color="#185EA9"
                      style="min-width: 36px; padding: 0;"
                      :disabled="isReadOnly || item.order === selectedCourses.length - 1"
                      @click="moveCourse(item.order, 1)"
                    >
                      <v-icon color="#185EA9">arrow_downward</v-icon>
                    </v-btn>
                    <v-btn
                      text
                      color="#ff0000"
                      style="min-width: 36px; padding: 0;"
                      :disabled="isReadOnly"
                      @click="removeCourse(item.order)"
                    >
                      <v-icon color="#ff0000">delete</v-icon>
                    </v-btn>
                  </template>
                  <template v-slot:no-data>
                    <span style="color: #9e9e9e;">
                      {{ $t("wkt.admin.training-program.edit-window.no-courses") }}
                    </span>
                  </template>
                </v-data-table>
              </v-col>
            </v-row>
          </v-container>
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions>
          <v-btn
            v-if="program.id"
            color="#ff0000"
            @click="confirmDelete()"
            :disabled="isReadOnly"
          >
            <v-icon left color="#ffffff">delete</v-icon>
            <span style="color: #ffffff;">{{
              $t("wkt.admin.common.dialog-buttons.delete").toUpperCase()
            }}</span>
          </v-btn>
          <v-spacer></v-spacer>
          <v-btn
            color="#185EA9"
            text
            @click="onSave()"
            style="font-size: 0.9em; font-weight: 400;"
            :disabled="isReadOnly"
          >
            {{ $t("wkt.admin.common.dialog-buttons.ok").toUpperCase() }}
          </v-btn>
          <v-btn
            text
            @click="showDialog = false"
            style="font-size: 0.9em; font-weight: 400;"
          >
            {{ $t("wkt.admin.common.dialog-buttons.cancel").toUpperCase() }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <ConfirmDialog
      ref="dlgConfirm"
      v-bind:title="$t('wkt.admin.training-program.edit-window.confirm-dialog.title')"
      v-bind:message="$t('wkt.admin.training-program.edit-window.confirm-dialog.text')"
      v-bind:titleOk="$t('wkt.admin.common.dialog-buttons.delete')"
      v-bind:titleCancel="$t('wkt.admin.common.dialog-buttons.cancel')"
      v-on:confirmed="deleteItem()"
    />
  </v-row>
</template>

<script>
import BaseModule from "../../common/BaseModule.js";
import ConfirmDialog from "../../common/ConfirmDialog";
import api from "../../../api/worktime.js";
import gwt from "../../../api/gwt.js";
import { Constants } from "../../common/Constants.js";

export default {
  name: "TrainingProgramDialog",
  mixins: [BaseModule],
  components: {
    ConfirmDialog,
  },
  data() {
    return {
      showDialog: false,
      loading: true,
      profile: null,
      program: {},
      trainingCourses: [],
      selectedCourses: [],
      selectedCourseId: null,
    };
  },
  computed: {
    sessionModuleTraining() {
      return this.profile?.moduleTraining;
    },
    userHasAnyAccess() {
      return this.sessionModuleTraining !== Constants.DENY_ALL;
    },
    isReadOnly() {
      return this.sessionModuleTraining === Constants.ALLOW_READ_ONLY;
    },
    availableCourses() {
      const selectedIds = this.selectedCourses.map((item) => item.courseId);
      return this.trainingCourses
        .filter((course) => !selectedIds.includes(course.id))
        .map((course) => ({
          ...course,
          courseLabel: course.reference
            ? `${course.reference} - ${course.name}`
            : course.name,
        }));
    },
    selectedCourseRows() {
      return this.selectedCourses.map((item, index) => {
        const course = this.trainingCourses.find((c) => c.id === item.courseId) || {};
        return {
          ...item,
          order: index,
          reference: course.reference || "",
          name: course.name || "",
        };
      });
    },
    courseHeaders() {
      return [
        { text: "#", value: "order", sortable: false, width: "8%" },
        { text: this.$t("wkt.admin.training-program.table.headers.reference"), value: "reference", sortable: false, width: "20%" },
        { text: this.$t("wkt.admin.training-program.table.headers.course-name"), value: "name", sortable: false, width: "52%" },
        { text: "", value: "actions", sortable: false, width: "20%" },
      ];
    },
  },
  methods: {
    // eslint-disable-next-line no-unused-vars
    onWheel(event) {
      if (!event) {
        return
      }

      event.stopPropagation()
    },
    open(action, payload) {
      this.profile = gwt.getSessionProfile();

      if (!this.userHasAnyAccess) {
        this.$emit("show-alert", {
          text: this.$t("wkt.admin.common.error.try_again_later"),
          color: "error",
          timeout: 3000,
        });
        return;
      }

      this.showDialog = true;
      this.selectedCourseId = null;

      if (this.$refs.form) {
        this.$refs.form.resetValidation();
      }

      this.loading = true;
      this.loadTrainingCourses(() => {
        if (!payload) {
          this.program = {
            companyId: this.profile.companyId,
            isActive: true,
            isSequential: false,
          };
          this.selectedCourses = [];
          this.loading = false;
          return;
        }

        api.getTrainingProgramById(
          payload,
          (res) => {
            this.program = res.program || {};
            this.selectedCourses = res.courses || [];
            this.loading = false;
          },
          (error) => {
            this.$emit("show-alert", {
              text: error && error.message ? error.message : error,
              color: "error",
              timeout: 3000,
            });
            this.loading = false;
          },
        );
      });
    },
    loadTrainingCourses(callback) {
      api.getTrainingCourses(
        (res) => {
          this.trainingCourses = res || [];
          callback();
        },
        (error) => {
          this.$emit("show-alert", {
            text: error && error.message ? error.message : error,
            color: "error",
            timeout: 3000,
          });
          this.loading = false;
        },
      );
    },
    addCourse() {
      if (!this.selectedCourseId) {
        return;
      }
      this.selectedCourses.push({
        courseId: this.selectedCourseId,
        sortOrder: this.selectedCourses.length,
      });
      this.selectedCourseId = null;
    },
    removeCourse(index) {
      this.selectedCourses.splice(index, 1);
    },
    moveCourse(index, step) {
      const targetIndex = index + step;
      if (targetIndex < 0 || targetIndex >= this.selectedCourses.length) {
        return;
      }
      const next = [...this.selectedCourses];
      const temp = next[index];
      next[index] = next[targetIndex];
      next[targetIndex] = temp;
      this.selectedCourses = next;
    },
    onSave() {
      if (!this.$refs.form.validate()) {
        return;
      }

      this.loading = true;
      const payload = {
        program: this.program,
        courses: this.selectedCourses.map((item, index) => ({
          courseId: item.courseId,
          sortOrder: index,
        })),
      };

      api.storeTrainingProgram(
        payload,
        (res) => {
          this.loading = false;
          if (res.rsOK) {
            this.showDialog = false;
            this.$emit("refresh");
          } else {
            this.$emit("show-alert", {
              text: res.message,
              color: "error",
              timeout: 3000,
            });
          }
        },
        (error) => {
          this.loading = false;
          this.$emit("show-alert", {
            text: error && error.message ? error.message : error,
            color: "error",
            timeout: 3000,
          });
        },
      );
    },
    confirmDelete() {
      this.$refs.dlgConfirm.confirm(this.program);
    },
    deleteItem() {
      this.loading = true;

      api.getTrainingProgramUsageCount(
        this.program.id,
        (res) => {
          if (res > 0) {
            this.loading = false;
            this.$emit("show-alert", {
              text: this.$t(
                "wkt.admin.training-program.edit-window.associated_items_error",
                [res],
              ),
              color: "error",
              timeout: 3000,
            });
            return;
          }

          api.deleteTrainingProgram(
            this.program.id,
            (deleteRes) => {
              this.loading = false;
              this.showDialog = false;

              if (!deleteRes.rsOK) {
                this.$emit("show-alert", {
                  text: deleteRes.message,
                  color: "error",
                  timeout: 3000,
                });
                return;
              }

              this.$emit("refresh");
            },
            (error) => {
              this.loading = false;
              this.$emit("show-alert", {
                text: error && error.message ? error.message : error,
                color: "error",
                timeout: 3000,
              });
            },
          );
        },
        (error) => {
          this.loading = false;
          this.$emit("show-alert", {
            text: error && error.message ? error.message : error,
            color: "error",
            timeout: 3000,
          });
        },
      );
    },
  },
};
</script>
